import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { navForRole, navForUser, allowedPaths, allowedPathsForUser, PROFESSIONAL_ROLES, KITCHEN_ROLES } from "../lib/constants";
import { TOUR_ANCHORS, tourDoneKey } from "../lib/tourAnchors";
import GuidedTour from "./GuidedTour";
import IncomingCall from "./IncomingCall";
import Avatar from "./Avatar";
import ProfilePhotoDialog from "./ProfilePhotoDialog";
import { LanguageSwitcher } from "./LanguageSwitcher";
import * as Icons from "lucide-react";
import {
  ClipboardCheck, LogOut, Menu, X, Bell, ChevronRight, Building2, Users2,
  AlertTriangle, Info, AlertCircle, ShieldAlert, History, ChevronDown,
  Search, Users, UserPlus, MessageSquareHeart, BriefcaseBusiness, Loader2,
  GraduationCap, HelpCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";

const SEARCH_TYPE = {
  resident: { Icon: Users, color: "#10b981" },
  employee: { Icon: BriefcaseBusiness, color: "#f59e0b" },
  family: { Icon: MessageSquareHeart, color: "#ec4899" },
  prospect: { Icon: UserPlus, color: "#8b5cf6" },
  formation: { Icon: GraduationCap, color: "#a855f7" },
};

const NAV_KEY = {
  "/": "dashboard", "/agenda": "agenda", "/residents": "residents", "/familles": "families",
  "/prospects": "prospects", "/rh": "hr", "/facturation": "billing", "/courriers": "mail",
  "/qualite": "quality", "/formation": "training", "/incontinence": "incontinence",
  "/statistiques": "statistics", "/utilisateurs": "users", "/historique": "history",
  "/suivi-commercial": "crm", "/tableau-ceo": "ceoDashboard", "/planning-activite": "planningActivite",
  "/categories-activites": "categories",
  "/vie-sociale": "vieSociale",
  "/tableau-pp": "ppDashboard",
  "/recommandations": "recommandations",
  "/domaines-pp": "domaines",
  "/objectifs-types-pp": "objectifsPP",
  "/cuisine-agenda": "cuisineAgenda",
  "/cuisine-planning": "cuisinePlanning",
  "/menu-semaine": "menuSemaine",
  "/cuisine-inventaire": "cuisineInventaire",
  "/menu-plats": "menuPlats",
  "/petit-dejeuner": "petitDejeuner",
  "/repas-preparation": "repasPreparation",
  "/habitants": "habitants",
  "/stats-repas": "statsRepas",
  "/messagerie": "messagerie",
  "/guide": "userGuide",
};

function GlobalSearch({ mobile = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const isAdmin = user?.role === "admin";
  const [adminCache, setAdminCache] = useState({ users: [], groups: [] });

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([api.get("/users"), api.get("/groups")])
      .then(([u, g]) => setAdminCache({ users: u.data || [], groups: g.data || [] }))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    if (isAdmin) {
      const low = term.toLowerCase();
      const gRes = adminCache.groups.filter((g) => (g.name || "").toLowerCase().includes(low))
        .map((g) => ({ type: "group", id: g.id, title: g.name, subtitle: t("users.groups.groups"), path: "/utilisateurs", groupId: g.id }));
      const uRes = adminCache.users.filter((u) => (u.name || "").toLowerCase().includes(low) || (u.username || "").toLowerCase().includes(low))
        .map((u) => ({ type: "user", id: u.id, title: u.name, subtitle: `${u.username} · ${u.group_name || u.maison || ""}`, path: "/utilisateurs", userId: u.id, groupId: u.group_id }));
      setResults([...gRes, ...uRes]);
      setOpen(true); setLoading(false);
      return;
    }
    setLoading(true);
    const to = setTimeout(async () => {
      try {
        const res = await api.get("/search", { params: { q: term } });
        setResults(res.data.results || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(to);
  }, [q, isAdmin, adminCache, t]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r) => {
    setOpen(false); setQ("");
    if (r.type === "group") {
      localStorage.setItem("activeGroupId", r.groupId || "all");
      window.dispatchEvent(new Event("carepilot:group"));
      navigate("/utilisateurs");
      return;
    }
    if (r.type === "user") {
      sessionStorage.setItem("highlightUser", r.userId);
      navigate("/utilisateurs");
      return;
    }
    navigate(r.path);
  };

  return (
    <div ref={boxRef} className={mobile ? "relative w-full" : "relative hidden md:block w-[300px] lg:w-[360px]"} data-testid={mobile ? "global-search-mobile" : "global-search"}>
      <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        placeholder={isAdmin ? t("common.searchUsersGroups", { defaultValue: "Rechercher un utilisateur ou un groupe…" }) : t("common.searchPlaceholder")}
        className="w-full glass rounded-xl h-11 pl-10 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-white/20 placeholder:text-muted-foreground/70"
      />
      {loading && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
      {!loading && q && (
        <button onClick={() => { setQ(""); setResults([]); setOpen(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="global-search-clear">
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      )}
      {open && q.trim().length >= 2 && (() => {
        const allowed = allowedPathsForUser(user);
        const shown = results.filter((r) => allowed.includes((r.path || "").split("?")[0]));
        return (
        <div className="absolute left-0 right-0 mt-2 max-h-[60vh] overflow-y-auto glass rounded-2xl card-3d z-50 p-2" data-testid="global-search-results">
          {shown.length === 0 && !loading && (
            <p className="text-sm text-muted-foreground px-3 py-6 text-center">Aucun résultat pour « {q.trim()} »</p>
          )}
          {shown.map((r) => {
            const cfg = SEARCH_TYPE[r.type] || SEARCH_TYPE.resident;
            const Icon = cfg.Icon;
            return (
              <button key={`${r.type}-${r.id}`} onClick={() => go(r)} data-testid={`search-result-${r.id}`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition text-left">
                <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: cfg.color + "22", color: cfg.color }}>
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-tight truncate">{r.title || "—"}</p>
                  <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>
                </div>
              </button>
            );
          })}
        </div>
        );
      })()}
    </div>
  );
}

function MaisonSelector({ mobile = false }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(localStorage.getItem("activeGroupId") || "all");
  const [active, setActive] = useState(localStorage.getItem("activeMaisonId") || "all");
  useEffect(() => {
    api.get("/my/maisons").then((r) => {
      const d = r.data;
      setData(d);
      if (d.role === "admin") { api.get("/groups").then((g) => setGroups(g.data || [])).catch(() => {}); return; }
      // Rôles groupe (non admin) : aucune option "Tout" — une maison sélectionnée par défaut
      if (d.can_switch && d.role !== "admin") {
        const ids = (d.maisons || []).map((m) => m.id);
        const cur = localStorage.getItem("activeMaisonId");
        if (ids.length && (!cur || cur === "all" || !ids.includes(cur))) {
          localStorage.setItem("activeMaisonId", ids[0]);
          setActive(ids[0]);
          window.location.reload();
        }
      }
    }).catch(() => {});
  }, []);
  const changeGroup = (val) => {
    localStorage.setItem("activeGroupId", val);
    setActiveGroup(val);
    window.dispatchEvent(new Event("carepilot:group"));
  };
  useEffect(() => {
    const sync = () => setActiveGroup(localStorage.getItem("activeGroupId") || "all");
    window.addEventListener("carepilot:group", sync);
    return () => window.removeEventListener("carepilot:group", sync);
  }, []);
  const change = (val) => {
    if (!val) return;
    localStorage.setItem("activeMaisonId", val);
    setActive(val);
    window.location.reload();
  };
  if (!data) return null;
  const isAdmin = data.role === "admin";
  // Admin : sélecteur de GROUPE (pas de maison) — filtre la page Utilisateurs
  if (isAdmin) {
    if (groups.length === 0) return null;
    return (
      <div className={mobile ? "relative w-full" : "relative hidden md:block"} data-testid={mobile ? "group-selector-mobile" : "group-selector"}>
        <select value={activeGroup} onChange={(e) => changeGroup(e.target.value)} data-testid={mobile ? "group-selector-select-mobile" : "group-selector-select"}
          className={`appearance-none glass rounded-xl h-11 pl-9 pr-9 text-sm font-medium cursor-pointer hover:bg-white/10 transition focus:outline-none ${mobile ? "w-full" : "max-w-[260px]"}`}>
          <option value="all">{t("users.allGroups", { defaultValue: "Tous les groupes" })}</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <Icons.Layers className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-violet-400" />
        <ChevronDown className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
      </div>
    );
  }
  if (!data.can_switch || (data.maisons || []).length <= 1) return null;
  const allLabel = t("layout.allMaisons");
  // Admin: regroupe par groupe + option "Toutes" ; rôles groupe: liste simple, une maison par défaut (pas de "Tout")
  const grouped = (data.maisons || []).reduce((acc, m) => {
    const g = m.group_name || t("users.groups.unclassified");
    (acc[g] = acc[g] || []).push(m);
    return acc;
  }, {});
  const groupNames = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
  return (
    <div className={mobile ? "relative group w-full" : "relative group hidden md:block"} data-testid={mobile ? "maison-selector-mobile" : "maison-selector"}>
      <select value={active} onChange={(e) => change(e.target.value)} data-testid={mobile ? "maison-selector-select-mobile" : "maison-selector-select"}
        className={`appearance-none glass rounded-xl h-11 pl-3 pr-9 text-sm font-medium cursor-pointer hover:bg-white/10 transition focus:outline-none ${mobile ? "w-full" : "max-w-[240px]"}`}>
        {isAdmin && <option value="all">{allLabel}</option>}
        {isAdmin
          ? groupNames.map((g) => (
              <optgroup key={g} label={g}>
                {grouped[g].map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </optgroup>
            ))
          : (data.maisons || []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <ChevronDown className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
    </div>
  );
}

const LEVEL_ICON = { urgent: AlertTriangle, warning: AlertCircle, info: Info, security: ShieldAlert };
const LEVEL_COLOR = { urgent: "#ef4444", warning: "#f59e0b", info: "#3b82f6", security: "#ec4899" };

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, items: [] });
  const [persist, setPersist] = useState({ unread_count: 0, items: [] });
  const [fullPostit, setFullPostit] = useState(null);
  const navigate = useNavigate();

  const NOTE_ROUTE = {
    prospect: "/suivi-commercial?tab=prospects",
    relance: "/suivi-commercial?tab=relances",
    visit: "/suivi-commercial?tab=visites",
    event: "/suivi-commercial?tab=occupation",
    room: "/suivi-commercial?tab=occupation",
    partner: "/suivi-commercial?tab=partenaires",
  };
  const goNote = (n) => {
    const r = NOTE_ROUTE[n.type];
    setOpen(false);
    if (r) navigate(r);
  };

  const load = async () => {
    try { const res = await api.get("/notifications"); setData(res.data); } catch {}
    try { const res = await api.get("/planning/notifications"); setPersist(res.data); } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const goPersist = async (n) => {
    // Post-it : afficher en plein écran + marquer lu & pris en compte automatiquement
    if ((n.type === "postit" || n.type === "postit_reminder") && n.ref_id) {
      setOpen(false);
      try { await api.post(`/planning/notifications/${n.id}/read`); } catch {}
      try { await api.post(`/planning/postits/${n.ref_id}/read`); } catch {}
      try { await api.post(`/planning/postits/${n.ref_id}/ack`); } catch {}
      try {
        const { data } = await api.get(`/planning/postits/${n.ref_id}`);
        setFullPostit(data);
      } catch {
        setFullPostit({ text: n.detail || "", author_name: (n.title || "").replace(/^.*·\s*/, ""), priority: n.level === "urgent" ? "urgent" : "standard" });
      }
      load();
      return;
    }
    setOpen(false);
    try {
      await api.post(`/planning/notifications/${n.id}/read`);
      if (n.type === "chat" && n.ref_id) await api.post(`/planning/chat/conversations/${n.ref_id}/read`);
    } catch {}
    load();
    if (n.link) navigate(n.link);
  };
  const readAllPersist = async () => {
    try { await api.post("/planning/notifications/read-all"); } catch {}
    load();
  };
  const markRead = async (n, e) => {
    if (e) e.stopPropagation();
    try { await api.post(`/planning/notifications/${n.id}/read`); } catch {}
    load();
  };

  const persistItems = (persist.items || []).filter((n) => !n.read);
  const totalCount = (data.count || 0) + (persist.unread_count || 0);
  const empty = totalCount === 0 && persistItems.length === 0;

  return (
    <>
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell"
        className="relative h-11 w-11 rounded-xl glass flex items-center justify-center hover:bg-white/10 transition">
        <Bell className="h-5 w-5" />
        {totalCount > 0 && (
          <span data-testid="notif-count" className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-pink-500 text-white text-[11px] font-bold flex items-center justify-center">
            {totalCount > 99 ? "99+" : totalCount}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[380px] max-h-[70vh] overflow-y-auto glass rounded-2xl card-3d z-50 p-2" data-testid="notif-panel">
            <div className="px-3 py-2 flex items-center justify-between">
              <span className="font-display font-bold">Notifications</span>
              <span className="text-xs text-muted-foreground">{totalCount} en attente</span>
            </div>

            {persistItems.length > 0 && (
              <div className="mb-2">
                <div className="px-3 pb-1 flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Messages & post-it</span>
                  <button onClick={readAllPersist} data-testid="notif-readall" className="text-[11px] font-semibold text-teal-400 hover:text-teal-300 inline-flex items-center gap-1">
                    <Icons.CheckCheck className="h-3 w-3" />Tout lu
                  </button>
                </div>
                <div className="space-y-1">
                  {persistItems.slice(0, 12).map((n) => (
                    <div key={n.id} data-testid={`notif-persist-${n.id}`}
                      className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl transition hover:bg-white/10 group">
                      <button type="button" onClick={() => goPersist(n)} className="flex items-start gap-3 min-w-0 flex-1 text-left cursor-pointer">
                        <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: (LEVEL_COLOR[n.level] || "#eab308") + "22", color: LEVEL_COLOR[n.level] || "#eab308" }}>
                          {n.type === "chat" ? <Icons.MessageCircle className="h-4 w-4" /> : n.type === "postit_reminder" ? <Icons.AlarmClock className="h-4 w-4" /> : n.type === "activite" ? <Icons.CalendarPlus className="h-4 w-4" /> : <Icons.StickyNote className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold leading-tight flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-pink-500 shrink-0" />{n.title}</p>
                          {n.detail && <p className="text-xs text-muted-foreground truncate">{n.type === "chat" && n.count > 1 ? `${n.count} nouveaux messages · ${n.detail}` : n.detail}</p>}
                        </div>
                      </button>
                      <button type="button" onClick={(e) => markRead(n, e)} data-testid={`notif-read-${n.id}`} title="Marquer comme lu"
                        className="shrink-0 self-center text-teal-400 hover:text-teal-300 opacity-70 group-hover:opacity-100 transition"><Icons.Check className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
                {data.items.length > 0 && <div className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Rappels</div>}
              </div>
            )}

            {empty && <p className="text-sm text-muted-foreground px-3 py-8 text-center">Tout est à jour 🎉</p>}
            <div className="space-y-1">
              {data.items.map((n, i) => {
                const Icon = LEVEL_ICON[n.level] || Info;
                const clickable = !!NOTE_ROUTE[n.type];
                return (
                  <button key={i} type="button" onClick={() => goNote(n)} disabled={!clickable}
                    data-testid={`notif-item-${n.type}`}
                    className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-xl transition ${clickable ? "hover:bg-white/10 cursor-pointer" : "cursor-default"}`}>
                    <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: LEVEL_COLOR[n.level] + "22", color: LEVEL_COLOR[n.level] }}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold leading-tight">{n.title}</p>
                      {n.detail && <p className="text-xs text-muted-foreground truncate">{n.detail}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
    {fullPostit && (
      <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="postit-fullscreen" onClick={() => setFullPostit(null)}>
        <div onClick={(e) => e.stopPropagation()}
          className={`relative w-full max-w-3xl rounded-3xl p-8 sm:p-12 shadow-2xl ${fullPostit.priority === "urgent" ? "bg-red-50 ring-4 ring-red-300" : fullPostit.priority === "important" ? "bg-orange-50 ring-4 ring-orange-300" : "bg-amber-50 ring-4 ring-amber-300"}`}>
          <button onClick={() => setFullPostit(null)} data-testid="postit-fullscreen-close"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-slate-600 shadow"><X className="h-5 w-5" /></button>
          <div className="flex items-center gap-2 mb-4">
            <Icons.StickyNote className={`h-6 w-6 ${fullPostit.priority === "urgent" ? "text-red-500" : fullPostit.priority === "important" ? "text-orange-500" : "text-amber-500"}`} />
            <span className={`text-sm font-extrabold uppercase tracking-widest ${fullPostit.priority === "urgent" ? "text-red-600" : fullPostit.priority === "important" ? "text-orange-600" : "text-amber-600"}`}>Post-it{fullPostit.priority === "urgent" ? " · Urgent" : fullPostit.priority === "important" ? " · Important" : ""}</span>
          </div>
          <p className="text-2xl sm:text-3xl font-semibold text-slate-800 leading-relaxed whitespace-pre-wrap break-words" data-testid="postit-fullscreen-text">{fullPostit.text}</p>
          {fullPostit.author_name && <p className="mt-6 text-sm text-slate-500">— {fullPostit.author_name}{fullPostit.date ? ` · ${fullPostit.date}` : ""}</p>}
          <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white px-4 py-2 text-sm font-bold" data-testid="postit-fullscreen-status">
            <Icons.CheckCheck className="h-4 w-4" /> Marqué comme lu et pris en compte
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false); // sidebar hidden by default

  const route = location.pathname;
  const hasTour = !!TOUR_ANCHORS[route];
  const [tourOpen, setTourOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  // Le tour ne s'auto-lance plus (source de tremblement selon l'environnement) : il s'ouvre via le bouton « ? ».
  useEffect(() => { setTourOpen(false); }, [route]);

  const closeTour = () => { localStorage.setItem(tourDoneKey(route), "1"); setTourOpen(false); };

  // Heartbeat de présence (pastille "en ligne" dans la messagerie)
  useEffect(() => {
    if (!user || user.role === "partenaire") return;
    const ping = () => api.post("/planning/presence/ping").catch(() => {});
    ping();
    const t = setInterval(ping, 30000);
    return () => clearInterval(t);
  }, [user]);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const nav = navForUser(user);

  return (
    <div className="min-h-screen app-bg">
      <IncomingCall />
      {/* Top bar */}
      <header className="sticky top-0 z-30 h-[68px] px-4 sm:px-6 flex items-center justify-between glass border-b border-white/10 no-print">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button onClick={() => setOpen(true)} data-testid="open-menu" className="h-11 w-11 rounded-xl glass flex items-center justify-center hover:bg-white/10 transition shrink-0">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/carepilot-64.png" alt="CarePilot" className="h-9 w-9 rounded-xl shrink-0 shadow-lg" data-testid="brand-logo" />
            <div className="leading-none min-w-0">
              <div className="font-display font-extrabold tracking-tight truncate">CarePilot</div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate"><Building2 className="h-3 w-3 shrink-0" /> <span className="truncate">{user?.maison}</span></div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {!(user && (PROFESSIONAL_ROLES.includes(user.role) || KITCHEN_ROLES.includes(user.role))) && <GlobalSearch />}
          {user?.role !== "partenaire" && <MaisonSelector />}
          {hasTour && route !== "/agenda" && (
            <button onClick={() => setTourOpen(true)} data-testid="tour-help-btn" title={t("tour.help")}
              className="h-11 w-11 rounded-xl glass flex items-center justify-center hover:bg-sky-500/15 text-sky-300 transition">
              <HelpCircle className="h-5 w-5" />
            </button>
          )}
          <LanguageSwitcher variant="dark" />
          <NotificationBell />
          <div className="hidden sm:flex items-center gap-2.5 pl-2">
            <button onClick={() => setProfileOpen(true)} data-testid="profile-avatar-btn" className="relative group" title="Photo de profil">
              <Avatar url={user?.avatar_url} name={user?.name} size={40} ring />
              <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-sky-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"><Icons.Camera className="h-2.5 w-2.5" /></span>
            </button>
            <div className="min-w-0 leading-tight">
              <div className="text-sm font-semibold truncate max-w-[140px]">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                {t(`roles.${user?.role}`)}
                {user?.role === "ceo" && (
                  <span data-testid="readonly-badge" className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ color: "#fcd34d", background: "rgba(245,158,11,0.16)" }}>
                    <Icons.Eye className="h-2.5 w-2.5" /> {t("layout.readOnly", { defaultValue: "Lecture seule" })}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={doLogout} data-testid="logout-btn" title={t("common.logout")} className="hover:bg-white/10">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Slide-over sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 no-print">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-md" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[290px] glass border-r border-white/10 flex flex-col animate-fade-up">
            <div className="flex items-center justify-between px-5 h-[68px] border-b border-white/10">
              <span className="font-display font-extrabold tracking-tight">Menu</span>
              <button onClick={() => setOpen(false)} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center"><X className="h-5 w-5" /></button>
            </div>
            {/* Contrôles mobiles (recherche + sélecteur résidence) — masqués sur md+ car présents dans l'en-tête */}
            <div className="md:hidden px-3 pt-4 space-y-2.5" data-testid="mobile-controls">
              {user?.role !== "partenaire" && !PROFESSIONAL_ROLES.includes(user?.role) && !KITCHEN_ROLES.includes(user?.role) && <GlobalSearch mobile />}
              {user?.role !== "partenaire" && <MaisonSelector mobile />}
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1.5">
              {nav.map((item) => {
                const Icon = Icons[item.icon] || Icons.Circle;
                return (
                  <NavLink key={item.to} to={item.to} end={item.to === "/"} onClick={() => setOpen(false)}
                    data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 px-3 min-h-[50px] rounded-xl text-[15px] font-medium transition-all ${
                        isActive ? "text-white" : "text-foreground/75 hover:bg-white/5"
                      }`}
                    style={({ isActive }) => isActive ? { background: item.color + "26", boxShadow: `inset 0 0 0 1px ${item.color}55` } : {}}
                  >
                    <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: item.color + "22", color: item.color }}>
                      <Icon className="h-5 w-5" />
                    </span>
                    {t(`nav.${NAV_KEY[item.to] || item.label}`)}
                    <ChevronRight className="h-4 w-4 ml-auto opacity-0 group-hover:opacity-50 transition" />
                  </NavLink>
                );
              })}
            </nav>
            <div className="p-3 border-t border-white/10 space-y-1.5">
              {user?.role !== "partenaire" && (
                <NavLink to="/guide" data-testid="guide-link"
                  className={({ isActive }) => `flex items-center gap-3 px-3 h-12 w-full rounded-xl transition ${isActive ? "bg-sky-500/15 text-sky-200" : "hover:bg-sky-500/10 text-sky-300"}`}>
                  <Icons.BookOpen className="h-5 w-5" /> {t("nav.guide")}
                </NavLink>
              )}
              <button onClick={doLogout} className="flex items-center gap-3 px-3 h-12 w-full rounded-xl hover:bg-white/5 text-foreground/80 transition">
                <LogOut className="h-5 w-5" /> {t("common.logout")}
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="min-w-0">{children}</main>
      <GuidedTour route={route} open={tourOpen} onClose={closeTour} />
      <ProfilePhotoDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
